"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { useRouter } from "@/lib/utm/hooks";
import Image from "next/image";
import api from "@/lib/api";
import { ApiRoutes } from "@/lib/routeResolver";
import SearchableSelect from "./SearchableSelect";
import FilterOverlayPanel from "./FilterOverlayPanel";
import PackageNameSearch from "./PackageNameSearch";
import RangeSlider from "./RangeSlider";
import styles from "./booking-form.module.css";

/* ── Types ─────────────────────────────────────────────────────── */

interface CountryOption {
  countryId: string;
  countryName: string;
  codeIso2Country: string;
}

interface ThemeOption {
  id: string;
  name: string;
}

interface ActiveFilters {
  packageName: string | null;
  destination: { id: string; name: string } | null;
  theme: { id: string; name: string } | null;
  duration: { min: number; max: number } | null;
  price: { min: number; max: number } | null;
}

const DURATION_MIN = 1;
const DURATION_MAX = 45;
const PRICE_MIN = 200;
const PRICE_MAX = 25000;

export default function BookingForm() {
  /* ── Data state ──────────────────────────────────────────────── */
  const [countries, setCountries] = useState<CountryOption[]>([]);
  const [themes, setThemes] = useState<ThemeOption[]>([]);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [loadingThemes, setLoadingThemes] = useState(true);

  /* ── Filter state ────────────────────────────────────────────── */
  const [packageName, setPackageName] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedTheme, setSelectedTheme] = useState("");

  const [durationOpen, setDurationOpen] = useState(false);
  const [durationApplied, setDurationApplied] = useState(false);
  const [durationMin, setDurationMin] = useState(DURATION_MIN);
  const [durationMax, setDurationMax] = useState(DURATION_MAX);
  const [draftDurMin, setDraftDurMin] = useState(DURATION_MIN);
  const [draftDurMax, setDraftDurMax] = useState(DURATION_MAX);

  const [priceOpen, setPriceOpen] = useState(false);
  const [priceApplied, setPriceApplied] = useState(false);
  const [priceMin, setPriceMin] = useState(PRICE_MIN);
  const [priceMax, setPriceMax] = useState(PRICE_MAX);
  const [draftPriceMin, setDraftPriceMin] = useState(PRICE_MIN);
  const [draftPriceMax, setDraftPriceMax] = useState(PRICE_MAX);

  const durationRef = useRef<HTMLDivElement>(null);
  const priceRef = useRef<HTMLDivElement>(null);

  /* ── Fetch countries ─────────────────────────────────────────── */
  useEffect(() => {
    let cancelled = false;
    api
      .get(ApiRoutes.INVENTORY_PACKAGE_COUNTRIES_WITH_PACKAGE)
      .then((res) => {
        if (!cancelled) {
          setCountries(res?.data?.data ?? []);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoadingCountries(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /* ── Fetch all themes ─────────────────────────────────────────── */
  useEffect(() => {
    let cancelled = false;
    api
      .get(ApiRoutes.FOUNDATION_PACKAGETHEME, {
        params: { PageSize: 10000 },
      })
      .then((res) => {
        if (!cancelled) {
          setThemes(res?.data?.data ?? []);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoadingThemes(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /* ── Derived select options ──────────────────────────────────── */
  const countryOptions = countries.map((c) => ({
    value: c.countryId,
    label: c.countryName,
  }));

  const themeOptions = themes.map((t) => ({
    value: t.id,
    label: t.name,
  }));

  /* ── Active filters ──────────────────────────────────────────── */
  const activeFilters: ActiveFilters = {
    packageName: packageName.trim() || null,
    destination: selectedCountry
      ? {
          id: selectedCountry,
          name:
            countries.find((c) => c.countryId === selectedCountry)
              ?.countryName ?? "",
        }
      : null,
    theme: selectedTheme
      ? {
          id: selectedTheme,
          name: themes.find((t) => t.id === selectedTheme)?.name ?? "",
        }
      : null,
    duration: durationApplied
      ? { min: durationMin, max: durationMax }
      : null,
    price: priceApplied
      ? { min: priceMin, max: priceMax }
      : null,
  };

  const hasActiveFilters = Object.values(activeFilters).some(Boolean);

  /* ── Search ─────────────────────────────────────────────────── */
  const router = useRouter();
  const [searching, setSearching] = useState(false);

  const handleSearch = useCallback(async () => {
    if (!hasActiveFilters || searching) return;
    setSearching(true);

    const params = new URLSearchParams();

    if (activeFilters.packageName) params.set("q", activeFilters.packageName);

    // If destination selected, call websitedestination to get page SEO URL
    if (selectedCountry) {
      try {
        const res = await api.get(ApiRoutes.WEBSITE_WEBSITEDESTINATION, {
          params: { DestinationId: selectedCountry },
        });
        const dest = res?.data?.data?.[0];
        const pageUrl = dest?.pageSEO?.pageURL as string | undefined;
        if (pageUrl) {
          params.set("destination", pageUrl);
        }
      } catch {
        // On error, clear destination and continue without it
      }
    }

    if (selectedTheme) {
      const themeName = themes.find((t) => t.id === selectedTheme)?.name;
      if (themeName) params.set("theme", themeName);
    }

    if (durationApplied) {
      params.set("nightsFrom", String(durationMin));
      params.set("nightsTo", String(durationMax));
    }

    if (priceApplied) {
      params.set("budgetFrom", String(priceMin));
      params.set("budgetTo", String(priceMax));
    }

    router.push(`/search?${params.toString()}`);
  }, [
    hasActiveFilters,
    searching,
    activeFilters.packageName,
    selectedCountry,
    selectedTheme,
    themes,
    durationApplied,
    durationMin,
    durationMax,
    priceApplied,
    priceMin,
    priceMax,
    router,
  ]);

  /* ── Handlers ────────────────────────────────────────────────── */
  const openDuration = useCallback(() => {
    setPriceOpen(false);
    setDraftDurMin(durationMin);
    setDraftDurMax(durationMax);
    setDurationOpen(true);
  }, [durationMin, durationMax]);

  const applyDuration = useCallback(() => {
    setDurationMin(draftDurMin);
    setDurationMax(draftDurMax);
    setDurationApplied(true);
    setDurationOpen(false);
  }, [draftDurMin, draftDurMax]);

  const resetDuration = useCallback(() => {
    setDraftDurMin(DURATION_MIN);
    setDraftDurMax(DURATION_MAX);
    setDurationMin(DURATION_MIN);
    setDurationMax(DURATION_MAX);
    setDurationApplied(false);
    setDurationOpen(false);
  }, []);

  const openPrice = useCallback(() => {
    setDurationOpen(false);
    setDraftPriceMin(priceMin);
    setDraftPriceMax(priceMax);
    setPriceOpen(true);
  }, [priceMin, priceMax]);

  const applyPrice = useCallback(() => {
    setPriceMin(draftPriceMin);
    setPriceMax(draftPriceMax);
    setPriceApplied(true);
    setPriceOpen(false);
  }, [draftPriceMin, draftPriceMax]);

  const resetPrice = useCallback(() => {
    setDraftPriceMin(PRICE_MIN);
    setDraftPriceMax(PRICE_MAX);
    setPriceMin(PRICE_MIN);
    setPriceMax(PRICE_MAX);
    setPriceApplied(false);
    setPriceOpen(false);
  }, []);

  const removeFilter = useCallback((key: keyof ActiveFilters) => {
    switch (key) {
      case "packageName":
        setPackageName("");
        break;
      case "destination":
        setSelectedCountry("");
        break;
      case "theme":
        setSelectedTheme("");
        break;
      case "duration":
        setDurationMin(DURATION_MIN);
        setDurationMax(DURATION_MAX);
        setDurationApplied(false);
        break;
      case "price":
        setPriceMin(PRICE_MIN);
        setPriceMax(PRICE_MAX);
        setPriceApplied(false);
        break;
    }
  }, []);

  const clearAll = useCallback(() => {
    setPackageName("");
    setSelectedCountry("");
    setSelectedTheme("");
    setDurationMin(DURATION_MIN);
    setDurationMax(DURATION_MAX);
    setDurationApplied(false);
    setPriceMin(PRICE_MIN);
    setPriceMax(PRICE_MAX);
    setPriceApplied(false);
  }, []);

  /* ── Format helpers ──────────────────────────────────────────── */
  const fmtNight = (n: number) => `${n} ${n === 1 ? "night" : "nights"}`;
  const fmtPrice = (n: number) =>
    n >= 1000 ? `$${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}k` : `$${n}`;

  const durationLabel =
    activeFilters.duration
      ? `${fmtNight(durationMin)} – ${fmtNight(durationMax)}`
      : "Duration";

  const priceLabel =
    activeFilters.price
      ? `${fmtPrice(priceMin)} – ${fmtPrice(priceMax)}`
      : "Price Range";

  return (
    <div className="booking-sec">
      <div className="container">
        <div className={styles.formWrap}>
          <div className={styles.searchGrid}>
            {/* ── Package Name ───────────────────────────────────── */}
            <div className={styles.nameRow}>
              <span className={styles.filterLabel}>Package Name</span>
              <PackageNameSearch
                size="lg"
                value={packageName}
                onChange={setPackageName}
                onSubmit={handleSearch}
              />
            </div>

            <div className={styles.filterRow}>
              {/* ── Destination ──────────────────────────────────── */}
              <div className={styles.filterGroup}>
                <div className={styles.filterIcon}>
                  <i className="fa-light fa-route" />
                </div>
                <div className={styles.filterBody}>
                  <span className={styles.filterLabel}>Destination</span>
                  <SearchableSelect
                    options={countryOptions}
                    value={selectedCountry}
                    onChange={setSelectedCountry}
                    placeholder={
                      loadingCountries ? "Loading..." : "Select Destination"
                    }
                    disabled={loadingCountries}
                  />
                </div>
              </div>

              {/* ── Theme ────────────────────────────────────────── */}
              <div className={styles.filterGroup}>
                <div className={styles.filterIcon}>
                  <i className="fa-regular fa-person-hiking" />
                </div>
                <div className={styles.filterBody}>
                  <span className={styles.filterLabel}>Theme</span>
                  <SearchableSelect
                    options={themeOptions}
                    value={selectedTheme}
                    onChange={setSelectedTheme}
                    placeholder={
                      loadingThemes ? "Loading..." : "Select Theme"
                    }
                    disabled={loadingThemes}
                  />
                </div>
              </div>

              {/* ── Duration ─────────────────────────────────────── */}
              <div className={styles.filterGroup} ref={durationRef}>
                <div className={styles.filterIcon}>
                  <i className="fa-light fa-clock" />
                </div>
                <div className={styles.filterBody}>
                  <span className={styles.filterLabel}>Duration</span>
                  <button
                    type="button"
                    className={`${styles.overlayTrigger} ${
                      durationOpen ? styles.overlayTriggerOpen : ""
                    } ${activeFilters.duration ? styles.overlayTriggerActive : ""}`}
                    onClick={openDuration}
                  >
                    <span>{durationLabel}</span>
                    <i
                      className={`fa-solid fa-chevron-down ${styles.triggerChevron} ${
                        durationOpen ? styles.triggerChevronOpen : ""
                      }`}
                    />
                  </button>
                  <FilterOverlayPanel
                    open={durationOpen}
                    onClose={() => setDurationOpen(false)}
                    title="Duration (nights)"
                    anchorRef={durationRef}
                    onApply={applyDuration}
                    onReset={resetDuration}
                  >
                    <RangeSlider
                      min={DURATION_MIN}
                      max={DURATION_MAX}
                      minValue={draftDurMin}
                      maxValue={draftDurMax}
                      onChange={(lo, hi) => {
                        setDraftDurMin(lo);
                        setDraftDurMax(hi);
                      }}
                      formatLabel={fmtNight}
                    />
                  </FilterOverlayPanel>
                </div>
              </div>

              {/* ── Price Range ──────────────────────────────────── */}
              <div className={styles.filterGroup} ref={priceRef}>
                <div className={styles.filterIcon}>
                  <i className="fa-light fa-tag" />
                </div>
                <div className={styles.filterBody}>
                  <span className={styles.filterLabel}>Price Range</span>
                  <button
                    type="button"
                    className={`${styles.overlayTrigger} ${
                      priceOpen ? styles.overlayTriggerOpen : ""
                    } ${activeFilters.price ? styles.overlayTriggerActive : ""}`}
                    onClick={openPrice}
                  >
                    <span>{priceLabel}</span>
                    <i
                      className={`fa-solid fa-chevron-down ${styles.triggerChevron} ${
                        priceOpen ? styles.triggerChevronOpen : ""
                      }`}
                    />
                  </button>
                  <FilterOverlayPanel
                    open={priceOpen}
                    onClose={() => setPriceOpen(false)}
                    title="Price Range (per person)"
                    anchorRef={priceRef}
                    onApply={applyPrice}
                    onReset={resetPrice}
                  >
                    <RangeSlider
                      min={PRICE_MIN}
                      max={PRICE_MAX}
                      minValue={draftPriceMin}
                      maxValue={draftPriceMax}
                      onChange={(lo, hi) => {
                        setDraftPriceMin(lo);
                        setDraftPriceMax(hi);
                      }}
                      step={100}
                      formatLabel={(v) =>
                        v.toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                          maximumFractionDigits: 0,
                        })
                      }
                    />
                  </FilterOverlayPanel>
                </div>
              </div>
            </div>

            {/* ── Search Button ────────────────────────────────── */}
            <div className={styles.filterBtnWrap}>
              <button
                type="button"
                className={styles.searchBtn}
                disabled={!hasActiveFilters || searching}
                onClick={handleSearch}
              >
                {searching ? (
                  <i className="fa-solid fa-spinner fa-spin" />
                ) : (
                  <Image
                    src="/main/img/icon/search.svg"
                    alt=""
                    width={16}
                    height={16}
                  />
                )}
                {searching ? "Searching..." : "Search"}
              </button>
            </div>
          </div>

          {/* ── Active Filter Chips ────────────────────────────── */}
          {hasActiveFilters && (
            <div className={styles.chipBar}>
              <span className={styles.chipLabel}>Filters:</span>

              {activeFilters.packageName && (
                <span className={styles.chip}>
                  <i className="fa-light fa-magnifying-glass" />
                  “{activeFilters.packageName}”
                  <button
                    type="button"
                    className={styles.chipRemove}
                    onClick={() => removeFilter("packageName")}
                    aria-label="Remove package name filter"
                  >
                    <i className="fa-solid fa-xmark" />
                  </button>
                </span>
              )}

              {activeFilters.destination && (
                <span className={styles.chip}>
                  <i className="fa-light fa-route" />
                  {activeFilters.destination.name}
                  <button
                    type="button"
                    className={styles.chipRemove}
                    onClick={() => removeFilter("destination")}
                    aria-label="Remove destination filter"
                  >
                    <i className="fa-solid fa-xmark" />
                  </button>
                </span>
              )}

              {activeFilters.theme && (
                <span className={styles.chip}>
                  <i className="fa-regular fa-person-hiking" />
                  {activeFilters.theme.name}
                  <button
                    type="button"
                    className={styles.chipRemove}
                    onClick={() => removeFilter("theme")}
                    aria-label="Remove theme filter"
                  >
                    <i className="fa-solid fa-xmark" />
                  </button>
                </span>
              )}

              {activeFilters.duration && (
                <span className={styles.chip}>
                  <i className="fa-light fa-clock" />
                  {fmtNight(activeFilters.duration.min)} –{" "}
                  {fmtNight(activeFilters.duration.max)}
                  <button
                    type="button"
                    className={styles.chipRemove}
                    onClick={() => removeFilter("duration")}
                    aria-label="Remove duration filter"
                  >
                    <i className="fa-solid fa-xmark" />
                  </button>
                </span>
              )}

              {activeFilters.price && (
                <span className={styles.chip}>
                  <i className="fa-light fa-tag" />
                  {fmtPrice(activeFilters.price.min)} –{" "}
                  {fmtPrice(activeFilters.price.max)}
                  <button
                    type="button"
                    className={styles.chipRemove}
                    onClick={() => removeFilter("price")}
                    aria-label="Remove price filter"
                  >
                    <i className="fa-solid fa-xmark" />
                  </button>
                </span>
              )}

              <button
                type="button"
                className={styles.clearAll}
                onClick={clearAll}
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
